import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { installStaleChunkRecovery, isStaleChunkError, reloadForStaleChunk } from "@/lib/staleChunkRecovery";
// Self-hosted Inter variable font (subset: latin) — ported from main.tsx.
import "@fontsource-variable/inter/wght.css";
import appCss from "../styles.css?url";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MainLayout } from "@/components/layout/MainLayout";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { LanguageProvider } from "@/components/i18n/LanguageProvider";
import { DEFAULT_LOCALE, parsePath, isEnabledLocale, matchBrowserLocale, localizedPath, getLocale, isTranslationIndexable } from "@/lib/i18n/locales";
import { hreflangLinks } from "@/lib/i18n/ssrSeo";
import { ogLocaleAlternates } from "@/lib/i18n/geoTargets";
import { ensureLocaleLoaded } from "@/lib/i18n";
import { AnalyticsTracker } from "@/components/analytics/AnalyticsTracker";
import { LanguageBanner } from "@/components/i18n/LanguageBanner";
import { ProfileCompletionGate } from "@/components/auth/ProfileCompletionGate";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { trackEvent } from "@/lib/analytics";
import { initSentry, captureError, sessionRequestId } from "@/lib/sentry";
import { initWebVitalsTracking } from "@/lib/webVitalsTracker";
import { SiteAgentTools } from "@/components/agents/SiteAgentTools";

// ---------------------------------------------------------------------------
// ported from main.tsx — client-only startup side effects.
// ---------------------------------------------------------------------------
if (typeof window !== "undefined") {
  // Language priority: saved preference → URL → browser → English. Only the
  // homepage auto-redirects to a non-English locale; a session guard prevents loops.
  (function redirectToPreferredLocale() {
    try {
      const { locale } = parsePath(window.location.pathname);
      if (locale !== DEFAULT_LOCALE) return;
      if (window.location.pathname !== "/") return;
      // Never move crawlers away from the canonical English homepage.
      if (/bot|crawl|spider|slurp|lighthouse|inspection|google|bing|yandex|baidu|duckduck|facebookexternalhit/i.test(navigator.userAgent)) return;
      if (sessionStorage.getItem("statlign_locale_redirected")) return;
      sessionStorage.setItem("statlign_locale_redirected", "1");
      let pref: string | null = null;
      try {
        pref = localStorage.getItem("statlign_locale");
      } catch {
        /* ignore */
      }
      const chosen = isEnabledLocale(pref)
        ? (pref as string)
        : matchBrowserLocale(navigator.languages ?? [navigator.language]);
      if (chosen && chosen !== DEFAULT_LOCALE) {
        window.location.replace(
          localizedPath(chosen, "/") + window.location.search + window.location.hash,
        );
      }
    } catch {
      /* never block startup on locale detection */
    }
  })();

  // Production error telemetry + Core Web Vitals persistence.
  initSentry();
  initWebVitalsTracking();

  // Advertise the MCP server (sign-in required) to JS-capable AI agents.
  try {
    if (!document.querySelector('link[rel="alternate"][data-mcp]')) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.type = "application/json";
      link.title = "Statlign MCP server";
      link.href = "https://statlign.com/mcp";
      link.setAttribute("data-mcp", "");
      document.head.appendChild(link);
    }
  } catch {
    /* discovery hint is best-effort */
  }

  // Surface otherwise-silent failures (rejected promises, non-React errors).
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? "unknown");
    console.error("[Statlign] Unhandled promise rejection:", reason);
    captureError(reason, { kind: "unhandledrejection", request_id: sessionRequestId() });
    try {
      trackEvent("client_error", { kind: "unhandledrejection", message: message.slice(0, 300) });
    } catch {
      /* never throw from a global handler */
    }
  });

  window.addEventListener("error", (event) => {
    const opaqueThirdParty =
      !event.error && !event.filename && event.lineno === 0 && /^script error\.?$/i.test(String(event.message ?? ""));
    if (opaqueThirdParty) return;
    console.error("[Statlign] Uncaught error:", event.error ?? event.message);
    captureError(event.error ?? event.message, { kind: "error", request_id: sessionRequestId() });
    try {
      trackEvent("client_error", { kind: "error", message: String(event.message ?? "").slice(0, 300) });
    } catch {
      /* never throw from a global handler */
    }
  });
}

/** Active locale, isomorphic: URL on the client, request basepath (ALS) on the server. */
function getActiveLocale(): string {
  if (typeof window !== "undefined") {
    return parsePath(window.location.pathname).locale;
  }
  const store = (
    globalThis as { __statlignLocaleBasepath?: { getStore: () => string | undefined } }
  ).__statlignLocaleBasepath;
  const bp = store?.getStore();
  return bp && bp !== "/" ? bp.replace(/^\//, "") : DEFAULT_LOCALE;
}

// ---------------------------------------------------------------------------
// Inline head scripts — ported verbatim from the Classic index.html.
// ---------------------------------------------------------------------------
const NOJS_FLIP_SCRIPT = `
document.documentElement.className = document.documentElement.className.replace(/\\bno-js\\b/, "js");
setTimeout(function () {
  if (!window.__statlignMounted) {
    document.documentElement.className =
      document.documentElement.className.replace(/\\bjs\\b/, "no-js");
  }
}, 8000);
`;

const CONSENT_DEFAULTS_SCRIPT = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
// Measurement model (2026-09-08): every visit is measured, in every region,
// from the first page view. Previously EEA/UK/Swiss traffic defaulted to
// denied, so Analytics received nothing at all from those regions. Visitors
// opt out from the notice or the footer's "Cookie settings" link, which stores
// a rejection and flips these signals to denied for that browser.
gtag('consent', 'default', {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
  personalization_storage: 'granted',
  functionality_storage: 'granted',
  security_storage: 'granted'
});
gtag('set', 'url_passthrough', true);
try {
  var stored = JSON.parse(localStorage.getItem('statlign_consent_v1') || 'null');
  if (stored && stored.status === 'rejected') {
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted'
    });
  }
} catch (e) { /* storage unavailable — defaults stand */ }
`;


// Bridge Google's own consent message (Funding Choices / TCF) into our stored
// choice, so a visitor who answers there is not asked again by our banner and
// analytics/advertising switch on immediately.
const CMP_BRIDGE_SCRIPT = `
(function () {
  function store(status) {
    try {
      var existing = JSON.parse(localStorage.getItem('statlign_consent_v1') || 'null');
      if (existing && existing.status) return;
      localStorage.setItem('statlign_consent_v1', JSON.stringify({
        status: status, updatedAt: new Date().toISOString(), version: 1
      }));
      window.dispatchEvent(new CustomEvent('statlign:consent-change'));
    } catch (e) { /* storage unavailable */ }
  }
  var tries = 0;
  var timer = setInterval(function () {
    tries++;
    if (tries > 40) clearInterval(timer);
    if (typeof window.__tcfapi !== 'function') return;
    clearInterval(timer);
    try {
      window.__tcfapi('addEventListener', 2, function (data, success) {
        if (!success || !data) return;
        if (data.eventStatus !== 'useractioncomplete' && data.eventStatus !== 'tcloaded') return;
        var purposes = (data.purpose && data.purpose.consents) || {};
        // Purpose 1 = store information on device, 7/8/9 = measurement.
        var accepted = !!(purposes[1] && (purposes[8] || purposes[7] || purposes[9]));
        if (data.eventStatus === 'useractioncomplete') store(accepted ? 'accepted' : 'rejected');
      });
    } catch (e) { /* CMP absent — our own banner still asks */ }
  }, 500);
})();
`;


const CMP_SCRIPT = `
(function () {
  try {
    if (/[?&]nothirdparty=1/.test(location.search)) return;
    if (['statlign.com', 'www.statlign.com'].indexOf(location.hostname) === -1) return;
    if (document.querySelector('script[data-statlign-cmp]')) return;
  } catch (e) { return; }
  var s = document.createElement('script');
  s.async = true;
  s.setAttribute('data-statlign-cmp', '1');
  s.src = 'https://fundingchoicesmessages.google.com/i/pub-9761423989942935?ers=1';
  document.head.appendChild(s);
  (function signalGooglefcPresent() {
    if (window.frames['googlefcPresent']) return;
    if (!document.body) return void setTimeout(signalGooglefcPresent, 0);
    var iframe = document.createElement('iframe');
    iframe.style = 'width:0;height:0;border:none;z-index:-1000;left:-1000px;top:-1000px;';
    iframe.style.display = 'none';
    iframe.name = 'googlefcPresent';
    document.body.appendChild(iframe);
  })();
})();
`;

const GOOGLE_TAGS_SCRIPT = `
window.dataLayer = window.dataLayer || [];
window.gtag = window.gtag || function(){dataLayer.push(arguments);};
// Only Google Tag Manager is loaded; GA4 (G-QJDQR23YPX) and the Google tag
// (GT-5773CXXR) are configured inside the GTM container and read dataLayer events.
(function () {
  if (window.__statlignGoogleTags) return;
  window.__statlignGoogleTags = 1;
  var loaded = false;
  function loadGoogleTags() {
    if (loaded) return;
    loaded = true;
    dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    // GA4 + Google Ads load directly so recording never depends on the GTM
    // container having tags. Page views are sent by the app (send_page_view false).
    gtag('js', new Date());
    gtag('config', 'G-QJDQR23YPX', { send_page_view: false });
    gtag('config', 'GT-5773CXXR');
    [['https://www.googletagmanager.com/gtm.js?id=GTM-TL3HXTNR', 'data-statlign-gtm'],
     ['https://www.googletagmanager.com/gtag/js?id=G-QJDQR23YPX', 'data-statlign-gtag']]
      .forEach(function (pair) {
        if (document.querySelector('script[' + pair[1] + ']')) return;
        var s = document.createElement('script');
        s.async = true;
        s.defer = true;
        s.setAttribute(pair[1], '1');
        s.src = pair[0];
        document.head.appendChild(s);
      });
  }
  // Mobile speed: Google tags cost ~1.5s of main-thread time on a mid-range
  // phone. Queue events in dataLayer and fetch the tags on the first real
  // interaction, or once the page has been idle for a few seconds after load.
  ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(function (ev) {
    window.addEventListener(ev, loadGoogleTags, { once: true, passive: true });
  });
  function idleLoad() {
    loadGoogleTags();
  }
  if (document.readyState === 'complete') idleLoad();
  else window.addEventListener('load', idleLoad, { once: true });
})();
`;

const TRUSTED_TYPES_SCRIPT = `
(function () {
  try {
    if (window.trustedTypes && window.trustedTypes.createPolicy) {
      if (!window.trustedTypes.defaultPolicy) {
        window.trustedTypes.createPolicy('default', {
          createHTML: function (s) { return s; },
          createScriptURL: function (s) { return s; },
          createScript: function (s) { return s; },
        });
      }
    }
  } catch (e) { /* never block startup */ }
})();
`;

// Third-party pop-under + social bar ads removed: pop-unders violate the Better Ads
// standards Google AdSense enforces and hurt search engagement signals.



const ORG_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://statlign.com/#organization",
      name: "Statlign",
      alternateName: "Statlign AI Sports Intelligence",
      url: "https://statlign.com/",
      description:
        "AI-powered sports intelligence platform delivering transparent, explainable predictions and analytics, including high-value odds across major sports and markets.",
      slogan: "Beyond Prediction. Into Intelligence.",
      foundingDate: "2024",
      knowsAbout: [
        "Sports predictions",
        "Sports betting analytics",
        "Odds analysis",
        "Match statistics",
        "Football predictions",
      ],
      logo: { "@type": "ImageObject", url: "https://statlign.com/og-image.jpg", width: 1200, height: 630 },
      image: "https://statlign.com/og-image.jpg",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        url: "https://statlign.com/",
        availableLanguage: ["English"],
      },
      sameAs: [
        "https://x.com/statlign",
        "https://www.linkedin.com/company/statlign",
        "https://www.instagram.com/statlign/",
      ],
    },
    {
      "@type": "SportsOrganization",
      "@id": "https://statlign.com/#sportsorganization",
      name: "Statlign",
      sport: ["Association football", "Sports analytics"],
      url: "https://statlign.com/",
      parentOrganization: { "@id": "https://statlign.com/#organization" },
    },
    {
      "@type": "WebSite",
      "@id": "https://statlign.com/#website",
      name: "Statlign",
      url: "https://statlign.com/",
      publisher: { "@id": "https://statlign.com/#organization" },
      description:
        "AI-powered sports intelligence and predictions, including high-value odds across major sports and markets.",
      inLanguage: "en",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://statlign.com/predictions?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "SiteNavigationElement",
      "@id": "https://statlign.com/#primary-nav",
      name: [
        "Today's Predictions",
        "AI Predictions",
        "High Odds",
        "Safe Bets",
        "Match Analysis",
        "Track Record",
        "Performance",
        "Blog",
        "Betting Guides",
        "FAQ",
      ],
      url: [
        "https://statlign.com/predictions/today",
        "https://statlign.com/ai-predictions",
        "https://statlign.com/high-odds",
        "https://statlign.com/safe-bets",
        "https://statlign.com/analysis",
        "https://statlign.com/track-record",
        "https://statlign.com/performance",
        "https://statlign.com/blog",
        "https://statlign.com/guides",
        "https://statlign.com/faq",
      ],
    },
  ],
});

// Critical CSS ported from index.html: dark base immediately + readable
// presentation for script-blocked visitors (`html.no-js`).
const CRITICAL_CSS = `
html, body { margin: 0; background: #0b0f14; color: #e5e7eb; }
html.no-js #root {
  max-width: 56rem; margin: 0 auto; padding: 1.25rem;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 1rem; line-height: 1.6;
}
html.no-js #root a { color: #93c5fd; }
html.no-js #root h1 { font-size: 1.9rem; line-height: 1.2; margin: 1rem 0 0.5rem; color: #f8fafc; }
html.no-js #root h2 { font-size: 1.4rem; margin: 1.5rem 0 0.5rem; color: #f8fafc; }
html.no-js #root h3 { font-size: 1.15rem; margin: 1.25rem 0 0.5rem; color: #f8fafc; }
html.no-js #root ul, html.no-js #root ol { padding-left: 1.25rem; }
html.no-js #root li { margin: 0.35rem 0; }
html.no-js #root img { max-width: 100%; height: auto; }
html.no-js #root nav a { display: inline-block; margin: 0 0.75rem 0.4rem 0; }
html.no-js #root table { width: 100%; border-collapse: collapse; display: block; overflow-x: auto; }
html.no-js #root th, html.no-js #root td {
  border: 1px solid #1e293b; padding: 0.4rem 0.5rem; text-align: left;
}
html.no-js details { margin: 0.5rem 0; }
html.no-js summary { cursor: pointer; color: #f8fafc; font-weight: 600; }
.nojs-gate, .nojs-fallback { display: none; }
html.no-js .nojs-gate, html.no-js .nojs-fallback {
  display: block; max-width: 56rem; margin: 1.5rem auto; padding: 1.25rem 1.5rem;
  border: 1px solid #1e293b; border-radius: 0.75rem; background: #111827;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
.nojs-gate h2, .nojs-fallback h2 { margin: 0 0 0.5rem; font-size: 1.15rem; color: #f8fafc; }
.nojs-gate p, .nojs-fallback p { margin: 0 0 0.75rem; color: #94a3b8; font-size: 0.95rem; line-height: 1.55; }
.nojs-gate a.nojs-cta, .nojs-fallback a.nojs-cta {
  display: inline-block; margin: 0 0.5rem 0.5rem 0; padding: 0.55rem 1rem;
  border-radius: 0.5rem; background: #3b82f6; color: #f8fafc;
  text-decoration: none; font-weight: 600;
}
.nojs-gate a, .nojs-fallback a { color: #93c5fd; }
.nojs-fallback ul { margin: 0; padding-left: 1.25rem; color: #cbd5e1; }
.nojs-fallback li { margin: 0.3rem 0; }
`;

const CSP_CONTENT =
  "default-src 'self'; script-src 'self' 'unsafe-inline' https://connect.facebook.net https://www.googletagmanager.com https://www.google-analytics.com https://news.google.com https://www.gstatic.com https://cdn.jsdelivr.net https://analytics.ahrefs.com https://pagead2.googlesyndication.com https://googleads.g.doubleclick.net https://tpc.googlesyndication.com https://adservice.google.com https://ep2.adtrafficquality.google https://fundingchoicesmessages.google.com https://cdn.chatway.app https://challenges.cloudflare.com https://*.lovable.app https://*.lovable.dev; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://cdn.chatway.app; img-src 'self' data: blob: https:; font-src 'self' data: https://fonts.gstatic.com https://cdn.chatway.app; connect-src 'self' https: wss:; media-src 'self' https: blob:; worker-src 'self' blob:; frame-src 'self' https:; base-uri 'self'; form-action 'self'; object-src 'none'; upgrade-insecure-requests;";

const SITE_ORIGIN = "https://statlign.com";

/**
 * Self-referencing canonical for the page currently being rendered.
 *
 * The root route used to hardcode `https://statlign.com/`, which made every
 * subpage advertise the homepage as its canonical — crawlers then folded those
 * pages into the homepage. TanStack concatenates `links` without dedup, so a
 * canonical here must be the ONLY one and must describe this exact request:
 * the pathname comes from the request on the server (ALS) and from the URL on
 * the client. `useSEO` later refines the same tag in place (adopt-not-duplicate).
 */
function currentCanonicalPath(): string {
  let path = "/";
  if (typeof window !== "undefined") {
    path = window.location.pathname || "/";
  } else {
    const store = (
      globalThis as { __statlignRequestPathname?: { getStore: () => string | undefined } }
    ).__statlignRequestPathname;
    path = store?.getStore() || "/";
  }
  // Drop the trailing slash (except the root) so one URL per page is advertised.
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Locale of the URL being rendered. Statlign serves one language per URL prefix
 * (English at the root, `/fr/...`, `/ar/...`), so `<html lang>`/`dir` and
 * `og:locale` must follow the request rather than being hardcoded to English.
 */
function currentLocaleDef() {
  return getLocale(parsePath(currentCanonicalPath()).locale);
}

/** Facebook-style locale tag ("fr_FR") for the current URL's language. */
function currentOgLocale(): string {
  const code = currentLocaleDef().code;
  return ogLocaleAlternates(code)[0] ?? code.replace("-", "_");
}

/**
 * Section-specific share card for the current URL, so links shared from
 * different parts of Statlign preview a matching picture. Leaf routes that set
 * their own og:image (sport hubs) still override this.
 */
const SECTION_IMAGES: Array<[RegExp, string, string]> = [
  [/^\/predictions\/nigeria-today/, "og-nigeria.jpg", "Nigerian football fans watching a match with Statlign picks"],
  [/^\/(track-record|performance|historical-odds|analysis)(\/|$)/, "og-track-record.jpg", "Statlign track record and performance charts"],
  [/^\/(premium|wallet|rewards|credit-receipts|ai-access|pricing)(\/|$)/, "og-premium.jpg", "Statlign credits and Premium membership"],
  [/^\/(blog|guides|faq|how-it-works|about|glossary)(\/|$)/, "og-guides.jpg", "Statlign football analysis guides"],
  [/^\/(predictions|ai-predictions|high-odds|safe-bets|jackpot-predictions|fixtures|leagues|competitions|clubs|teams|players|markets|countries|live|match-preview|explore|best-football-prediction-site|vs)(\/|$)/, "og-predictions.jpg", "Statlign AI football predictions and match data"],
];

function currentShareImage(): { url: string; alt: string } {
  const { path } = parsePath(currentCanonicalPath());
  const hit = SECTION_IMAGES.find(([re]) => re.test(path || "/"));
  return hit
    ? { url: `${SITE_ORIGIN}/${hit[1]}`, alt: hit[2] }
    : { url: `${SITE_ORIGIN}/og-image.jpg`, alt: "Statlign — AI-powered sports intelligence and predictions" };
}

const PRIVATE_PATH_RE = /^\/(auth|reset-password|settings|wallet|upload|admin|match-curation|apply|member|credit-receipts|unsubscribe|profile)(\/|$)/;

/**
 * Only English URLs are offered to search engines. Translated copies stay
 * reachable for visitors but point their canonical at the English page and
 * carry noindex so they stop competing with it. Private pages are noindex everywhere.
 */
function currentIndexing(search?: Record<string, unknown>): { robots: string; canonical: string } {
  let full = currentCanonicalPath();
  // Match previews are distinct pages per fixture — keep the teams in the canonical.
  if (full.endsWith("/match-preview") && search && search["home"] && search["away"]) {
    const q = new URLSearchParams({ home: String(search["home"]), away: String(search["away"]) });
    if (search["league"]) q.set("league", String(search["league"]));
    full = `${full}?${q.toString()}`;
  }
  const { locale, path } = parsePath(full);
  const englishPath = path || "/";
  const translated = locale !== DEFAULT_LOCALE;
  const priv = PRIVATE_PATH_RE.test(englishPath);
  // Fully translated pages (TRANSLATED_INDEXABLE_PATHS) are indexable and self-canonical.
  const openTranslation = translated && isTranslationIndexable(locale, englishPath);
  return {
    robots: priv
      ? "noindex, nofollow"
      : translated && !openTranslation
        ? "noindex, follow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    canonical: `${SITE_ORIGIN}${translated && !openTranslation ? englishPath : full}`,
  };
}



export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Register the page's translation dictionary before rendering so localized
  // URLs ship full content in the first HTML instead of an empty body.
  beforeLoad: async () => {
    await ensureLocaleLoaded(getActiveLocale());
  },
  head: ({ matches }) => ({
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      {
        name: "keywords",
        content:
          "sports predictions, AI sports tips, sports betting analytics, match predictions, high-value odds, football predictions, over/under tips, BTTS predictions, correct score, accumulator tips",
      },
      { name: "robots", content: currentIndexing(matches?.[matches.length - 1]?.search as Record<string, unknown>).robots },
      { name: "author", content: "Statlign" },
      { name: "application-name", content: "Statlign" },
      { name: "theme-color", content: "#0b0f14" },
      { name: "referrer", content: "origin-when-cross-origin" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "Statlign" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "msapplication-TileColor", content: "#0b0f14" },
      { name: "format-detection", content: "telephone=no" },
      { name: "google-site-verification", content: "iy0YX7bl4B8seyUUFdLKFoGw2SLzCNc_ZPafEVcCwM0" },
      // Bing/Yandex/Baidu verification codes go here once issued by each webmaster console.
      { name: "google-adsense-account", content: "ca-pub-9761423989942935" },
      {
        name: "ahrefs-site-verification",
        content: "7bc32ca045bc7d0212fc4e0478b16817580277116d17a23b29092b4116a29c71",
      },
      { name: "rating", content: "general" },
      // CSP is a valid http-equiv and is the only CSP the host delivers.
      // X-Content-Type-Options / Permissions-Policy are NOT valid http-equiv
      // values (W3C error) — they are sent as real headers in src/server.ts.
      { httpEquiv: "Content-Security-Policy", content: CSP_CONTENT },
      // og:type is set per leaf route (website/article) — a site-wide default
      // here would duplicate the tag on content routes.
      { property: "og:site_name", content: "Statlign" },
      { property: "og:locale", content: currentOgLocale() },
      { property: "og:url", content: `${SITE_ORIGIN}${currentCanonicalPath()}` },
      { property: "og:image", content: currentShareImage().url },
      { property: "og:image:secure_url", content: currentShareImage().url },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: currentShareImage().alt },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@statlign" },
      { name: "twitter:creator", content: "@statlign" },
      { name: "twitter:image", content: currentShareImage().url },
      { name: "twitter:image:alt", content: currentShareImage().alt },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Exactly ONE canonical, self-referencing for this request. TanStack
      // concatenates `links` across matches without dedup, so leaf routes must
      // not add their own; `useSEO` updates this tag in place on the client.
      { rel: "canonical", href: currentIndexing(matches?.[matches.length - 1]?.search as Record<string, unknown>).canonical },
      ...hreflangLinks(),
      { rel: "shortcut icon", href: "https://statlign.com/favicon.ico?v=statlign3", type: "image/x-icon" },
      { rel: "icon", href: "https://statlign.com/favicon.ico?v=statlign3", sizes: "any", type: "image/x-icon" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "https://statlign.com/icons/icon-192.png?v=statlign3" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "https://statlign.com/favicon.png?v=statlign3" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=statlign3" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
      { rel: "alternate", type: "text/plain", title: "llms.txt", href: "/llms.txt" },
      { rel: "alternate", type: "application/rss+xml", title: "Statlign Sports Intelligence Feed", href: "https://statlign.com/rss.xml" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
    ],
    scripts: [
      { children: NOJS_FLIP_SCRIPT },
      { children: CONSENT_DEFAULTS_SCRIPT },
      { children: CMP_SCRIPT },
      { children: CMP_BRIDGE_SCRIPT },

      { children: GOOGLE_TAGS_SCRIPT },
      { children: TRUSTED_TYPES_SCRIPT },
      { type: "application/ld+json", children: ORG_JSON_LD },
    ],
    styles: [{ children: CRITICAL_CSS }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang={(currentLocaleDef().code || "en").trim() || "en"} dir={currentLocaleDef().dir || "ltr"}
      // `dark` is emitted server-side so the very first paint (especially on
      // mobile, which Google crawls) is the intended dark theme instead of a
      // white flash that only turns dark after hydration.
      className="no-js dark"
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-TL3HXTNR"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
        <div id="root">{children}</div>
        {/* Script-off notice lives in <noscript> so this boilerplate never enters
            the indexed text or search snippets of every page. */}
        <noscript>
        <div className="nojs-gate">
          <h2>Scripts are blocked — you're reading the static version of Statlign</h2>
          <p>
            Your browser or an extension is blocking Statlign's scripts, so live odds, filters and
            interactive analysis can't run. Article text, predictions and league pages below still
            work without scripts.
          </p>
          <p>
            Statlign's free tier is funded by advertising. Statlign Premium removes ads entirely, so
            the site works fully with your blocker on.
          </p>
          <a className="nojs-cta" href="/premium">
            Get Statlign Premium
          </a>
          <a href="/auth">Sign in or create an account</a>
        </div>
        <div className="nojs-fallback">
          <h2>Readable without scripts</h2>
          <p>These sections are served as static pages and work with scripts blocked:</p>
          <ul>
            <li><a href="/">Statlign home</a></li>
            <li><a href="/predictions">Today's predictions</a></li>
            <li><a href="/high-odds">High odds portal</a></li>
            <li><a href="/safe-bets">Safe bets</a></li>
            <li><a href="/track-record">Track record</a></li>
            <li><a href="/guides">Betting guides</a></li>
            <li><a href="/blog">News and analysis</a></li>
            <li><a href="/faq">FAQ</a></li>
            <li><a href="/sitemap">Full site map</a></li>
          </ul>
        </div>
        </noscript>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const activeLocale = getActiveLocale();

  useEffect(() => {
    // Tells the no-js watchdog the app really mounted (ported from main.tsx).
    (window as unknown as { __statlignMounted?: number }).__statlignMounted = 1;
    installStaleChunkRecovery();
  }, []);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <QueryClientProvider client={queryClient}>
      <TimezoneProvider>
        <TooltipProvider>
          <LanguageProvider locale={activeLocale}>
            <AnalyticsTracker />
            <LanguageBanner />
            <ProfileCompletionGate />
            <Toaster />
            <Sonner />
            <NavigationProgress />
            <SiteAgentTools />
            <ErrorBoundary resetKey={pathname}>
              <Outlet />
            </ErrorBoundary>
          </LanguageProvider>
        </TooltipProvider>
      </TimezoneProvider>
    </QueryClientProvider>
  );
}

/** Thin top bar shown while the next page is loading, so clicks feel instant. */
function NavigationProgress() {
  const loading = useRouterState({ select: (s) => s.status === "pending" });
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 origin-left bg-primary transition-[transform,opacity] duration-300 ${
        loading ? "scale-x-75 opacity-100" : "scale-x-100 opacity-0"
      }`}
    />
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    if (isStaleChunkError(error)) reloadForStaleChunk();
  }, [error]);

  console.error("[Statlign] root error boundary:", error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold">This page didn't load</h1>
        <p className="text-muted-foreground text-sm">
          Something went wrong while rendering this page. You can try again or head back home.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a href="/" className="px-4 py-2 rounded-md border border-border text-foreground font-medium">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
